import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, Star } from "lucide-react";
import { IMAGES, SALON } from "@/data/salon";

const EASE = [0.76, 0, 0.24, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative h-[100svh] min-h-[640px] overflow-hidden flex items-end"
        >
            <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
                <img
                    src={IMAGES.hero}
                    alt="Styling elegante nel salone Moovie Parrucchieri"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-ink/40" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink/50 to-transparent" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 w-full max-w-[1440px] mx-auto px-5 md:px-10 pb-16 md:pb-20"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="text-cream/80 text-xs tracking-[0.3em] uppercase mb-5"
                    data-testid="hero-overline"
                >
                    Moovie Parrucchieri · Paese (Treviso)
                </motion.p>

                <h1
                    data-testid="hero-title"
                    className="font-serif uppercase text-cream leading-[0.92] tracking-tight text-[13.5vw] md:text-[9.5vw] lg:text-[8vw]"
                >
                    <MaskedLine delay={0.35}>Il tuo stile,</MaskedLine>
                    <MaskedLine delay={0.5}>
                        la nostra{" "}
                        <em className="italic font-medium text-[#E4B7BE]">cura.</em>
                    </MaskedLine>
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.15 }}
                    className="mt-8 flex flex-wrap items-center gap-4"
                >
                    <a
                        href={SALON.phoneHref}
                        data-testid="hero-cta-book"
                        className="group inline-flex items-center gap-3 bg-sage text-cream text-sm tracking-[0.1em] uppercase px-8 py-4 rounded-full hover:bg-cream hover:text-ink transition-colors duration-300"
                    >
                        Prenota il tuo appuntamento
                        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                    </a>
                    <a
                        href={SALON.phoneHref}
                        data-testid="hero-cta-call"
                        className="inline-flex items-center gap-2 border border-cream/50 text-cream text-sm tracking-[0.1em] uppercase px-8 py-4 rounded-full hover:bg-cream/10 transition-colors duration-300"
                    >
                        <Phone size={16} /> {SALON.phoneDisplay}
                    </a>
                    <div
                        data-testid="hero-rating-badge"
                        className="inline-flex items-center gap-2 bg-cream/10 backdrop-blur-md border border-cream/20 text-cream text-sm px-5 py-3 rounded-full"
                    >
                        <Star size={15} className="fill-[#E4B7BE] text-[#E4B7BE]" />
                        <span className="font-semibold">{SALON.rating}</span>
                        <span className="text-cream/70">su Google · {SALON.reviewCount} recensioni</span>
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                    className="mt-6 text-cream/70 text-sm md:text-base max-w-md"
                >
                    Riceviamo su appuntamento, con la flessibilità di chi ti conosce da anni.
                </motion.p>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-6 right-6 md:right-10 z-10 text-cream/60"
            >
                <ArrowDown size={20} className="animate-bounce" />
            </motion.div>
        </section>
    );
};

export default Hero;
